import { useEffect, useState } from 'react';

import { base64ToBlob } from './documentHelpers';

/** What `useBlobUrl` exposes: the live URL, plus a decode-failure flag. */
export interface BlobUrlState {
  /** Object URL for the payload, or `null` while none is minted (or on failure). */
  url: string | null;
  /** True when the base64 payload could not be decoded (`atob` threw). */
  failed: boolean;
}

const NO_URL: BlobUrlState = { url: null, failed: false };

/** Decode a payload into a `blob:` URL; a malformed one is reported, not thrown. */
function mintObjectUrl(b64: string | undefined, contentType: string): BlobUrlState {
  if (!b64) return NO_URL;
  try {
    return { url: URL.createObjectURL(base64ToBlob(b64, contentType)), failed: false };
  } catch {
    // `atob` throws on characters outside the base64 alphabet.
    return { url: null, failed: true };
  }
}

/**
 * Mint a `blob:` object URL for a base64 payload, revoked when the payload
 * changes and on unmount.
 *
 * Used where a preview must be **framed** rather than rendered inline: the
 * deployed CSP allows `frame-src 'self' blob:` but not `data:`, so a `data:`
 * URI in an `<iframe src>` is refused (see `docs/SECURITY-HEADERS.md`).
 *
 * The URL is minted inside the effect — never in a `useMemo` — so the
 * mint/revoke cycle is owned end to end by the same effect: under StrictMode's
 * double-invoked effects a memoised URL would be revoked by the first cleanup
 * and left dead in the DOM, whereas here the re-run mints a fresh one.
 */
export function useBlobUrl(
  b64: string | undefined,
  contentType: string,
): BlobUrlState {
  const [state, setState] = useState<BlobUrlState>(NO_URL);

  useEffect(() => {
    const next = mintObjectUrl(b64, contentType);

    // Handing the freshly-minted handle to React *is* this effect's purpose
    // (an external-system resource crossing into render), not derived state:
    // it runs once per payload and cannot cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- publishes the object URL minted above; one update per payload.
    setState(next);

    return () => {
      if (next.url) URL.revokeObjectURL(next.url);
    };
  }, [b64, contentType]);

  return state;
}
